import Typography from "@mui/joy/Typography";
import useOpenTask from "hooks/useOpenTask";
import { Box, Stack } from "@mui/joy";
import useGetTaskTitleFromTaskKey from "hooks/useGetTaskTitleFromTaskKey";
import useIsInappWebview from "hooks/useIsInappWebview";
import { theme } from "../../styles/theme";

const ThoughtmapIcon = () => {
  return (
    <svg
      width="21"
      height="20"
      viewBox="0 0 21 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.6666 12.5C12.2777 9.61324 8.38885 15.3867 1.99996 12.5"
        stroke="#B9D6DE"
        stroke-width="2"
        stroke-linecap="round"
      />
      <path
        d="M10.6765 18.0494C15.1184 12.6253 10.5482 7.37466 14.9901 1.95061"
        stroke="#8DABE1"
        stroke-width="2"
        stroke-linecap="round"
      />
      <path
        d="M17 7.49999C11.8889 4.61324 8.77778 10.3867 3.66667 7.5"
        stroke="#3760A9"
        stroke-width="2"
        stroke-linecap="round"
      />
      <path
        d="M6.10786 16.4395C10.2191 12.2497 5.44754 7.7503 9.55878 3.56049"
        stroke="#3760A9"
        stroke-width="2"
        stroke-linecap="round"
      />
    </svg>
  );
};

const ValueCompassIcon = () => {
  return (
    <svg
      width="21"
      height="20"
      viewBox="0 0 21 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clip-path="url(#clip0_2538_4437)">
        <circle cx="10.6666" cy="9.99999" r="9.16667" fill="#DFEEF2" />
        <path
          d="M10.6666 1V3"
          stroke="#B9D6DE"
          stroke-width="2"
          stroke-linecap="round"
        />
        <path
          d="M17.6666 10H19.6666"
          stroke="#B9D6DE"
          stroke-width="2"
          stroke-linecap="round"
        />
        <path
          d="M1.66663 10H3.66663"
          stroke="#B9D6DE"
          stroke-width="2"
          stroke-linecap="round"
        />
        <path
          d="M10.6666 17V19"
          stroke="#B9D6DE"
          stroke-width="2"
          stroke-linecap="round"
        />
        <mask
          id="mask0_2538_4437"
          style={{ maskType: "alpha" }}
          maskUnits="userSpaceOnUse"
          x="3"
          y="2"
          width="15"
          height="16"
        >
          <path
            d="M8.77475 6.5851L16.9014 2.97326C17.4035 2.75011 17.9165 3.26314 17.6934 3.76523L14.0815 11.8919C13.7799 12.5706 13.2372 13.1133 12.5585 13.4149L4.43185 17.0268C3.92977 17.2499 3.41673 16.7369 3.63988 16.2348L7.25173 8.10812C7.55337 7.42943 8.09605 6.88675 8.77475 6.5851Z"
            fill="#FF4A4A"
          />
        </mask>
        <g mask="url(#mask0_2538_4437)">
          <path
            d="M3.00634 17.6603L13.6129 12.9463L7.72038 7.05373L3.00634 17.6603Z"
            fill="#8DABE1"
          />
          <path
            d="M18.327 2.33968L13.6129 12.9463L7.72035 7.05372L18.327 2.33968Z"
            fill="#3B61A5"
          />
        </g>
      </g>
      <defs>
        <clipPath id="clip0_2538_4437">
          <rect
            width="20"
            height="20"
            fill="white"
            transform="translate(0.666626)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

const ProgressIcon = () => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <ellipse cx="10" cy="16.6667" rx="8.33333" ry="2.5" fill="#8DABE1" />
      <path
        d="M7.56301 2.13775C7.62353 1.86264 7.8673 1.66667 8.14899 1.66667H17.7744C18.3237 1.66667 18.5842 2.34357 18.1767 2.71185L15.2345 5.37046C14.9402 5.63638 14.8852 6.07777 15.1052 6.40779L16.6708 8.75624C17.0252 9.28789 16.6441 10 16.0052 10H6.57966C6.19633 10 5.91131 9.64546 5.99367 9.27109L7.56301 2.13775Z"
        fill="#3760A9"
      />
      <path
        d="M7.91665 1.66667L4.58331 16.6667"
        stroke="#B9D6DE"
        stroke-width="2"
        stroke-linecap="round"
      />
    </svg>
  );
};

export default function OverviewItem({ taskKey }: { taskKey: string }) {
  const openTask = useOpenTask({ taskKey: taskKey });
  const title = useGetTaskTitleFromTaskKey(taskKey);
  const isInappWebview = useIsInappWebview();

  return (
    <Stack
      direction={isInappWebview ? "column" : "row"}
      alignItems={isInappWebview ? "start" : "center"}
      spacing="10px"
      sx={{
        cursor: "pointer",
        px: "20px",
        borderRadius: "16px",
        ...(isInappWebview
          ? {
              py: "16px",
              backgroundColor: theme.vars.palette.Surface.Forward,
              color: theme.vars.palette.Content.Strong,

              "@media (min-width: 1024px)": {
                "&:hover": {
                  backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
                },
              },
              "@media (max-width: 1024px)": {
                "&:hover": {
                  backgroundColor: theme.vars.palette.Surface.Forward,
                  color: theme.vars.palette.Content.Strong,
                },
              },

              // 포커스 상태를 기본 상태와 동일하게 유지
              "&:focus": {
                backgroundColor: theme.vars.palette.Surface.Forward,
                color: theme.vars.palette.Content.Strong,
              },
              // 클릭(터치)하는 순간 (비활성화 상태가 아닐 때만)
              "&:not(.Mui-disabled):active": {
                backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
              },
            }
          : {
              height: "40px",
            }),
      }}
      onClick={() => {
        (document.activeElement as HTMLElement)?.blur();
        openTask();
      }}
    >
      <Box
        sx={{
          width: "20px",
          height: "20px",
          mr: "6px",
        }}
      >
        {taskKey === "thoughtmap" ? (
          <ThoughtmapIcon />
        ) : taskKey === "valueCompass" ? (
          <ValueCompassIcon />
        ) : taskKey === "progress" ? (
          <ProgressIcon />
        ) : (
          <></>
        )}
      </Box>
      <Typography
        variant={"Body"}
        sx={{
          flex: 1,
          fontWeight: 700,
          color: theme.vars.palette.Content.Strong,
          lineHeight: 1,
          whiteSpace: "nowrap",
          px: 0,
        }}
      >
        {title}
      </Typography>
    </Stack>
  );
}
